import { sumCounts } from './overviewFormat';

export interface TopWithRest<T> {
    top: T[];
    restCount: number;
}

// Largest first, ties in input order; zero counts are dropped and everything past `limit` folds into restCount.
export function takeTopByCount<T extends { count: number }>(
    items: readonly T[],
    limit: number
): TopWithRest<T> {
    const keep = Math.max(0, Math.floor(limit));
    const ranked = items.filter((item) => item.count > 0).sort((a, b) => b.count - a.count);
    return { top: ranked.slice(0, keep), restCount: sumCounts(ranked.slice(keep)) };
}

// Whole percentages that always add up to 100 (largest remainder), so a legend never shows 99% or 101%.
export function percentShares(counts: readonly number[]): number[] {
    const safe = counts.map((count) => (Number.isFinite(count) && count > 0 ? count : 0));
    const total = safe.reduce((sum, count) => sum + count, 0);
    if (total === 0) return safe.map(() => 0);
    const exact = safe.map((count) => (count / total) * 100);
    const floors = exact.map(Math.floor);
    const missing = 100 - floors.reduce((sum, value) => sum + value, 0);
    const bumped = new Set(
        exact
            .map((value, index) => ({ index, remainder: value - floors[index] }))
            .sort((a, b) => b.remainder - a.remainder || a.index - b.index)
            .slice(0, missing)
            .map(({ index }) => index)
    );
    return floors.map((value, index) => (bumped.has(index) ? value + 1 : value));
}

// A non-zero slice that rounds to 0% still reads as present.
export function shareLabel(percent: number, count: number): string {
    return count > 0 && percent === 0 ? '<1%' : `${percent}%`;
}
